import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function Empresa() {
  useEffect(() => {
    // SEO Meta Tags
    document.title = 'Nuestra Empresa - VaciadoPro Barcelona | Especialistas en Vaciado';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Conoce VaciadoPro Barcelona, empresa especializada en vaciado de pisos con más de 10 años de experiencia. Equipo profesional y servicio de calidad en Barcelona.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'empresa vaciado Barcelona, VaciadoPro Barcelona, experiencia vaciado pisos, equipo profesional Barcelona');
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/empresa`);
    }

    // Schema.org JSON-LD
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "Sobre VaciadoPro Barcelona",
      "description": "Información sobre nuestra empresa especializada en vaciado de pisos en Barcelona",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/empresa`,
      "mainEntity": {
        "@type": "Organization",
        "name": "VaciadoPro Barcelona",
        "description": "Empresa especializada en vaciado de pisos con más de 10 años de experiencia en Barcelona",
        "foundingDate": "2014",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Barcelona",
          "addressRegion": "Cataluña",
          "addressCountry": "ES"
        },
        "areaServed": {
          "@type": "City",
          "name": "Barcelona"
        }
      }
    });
    document.head.appendChild(schemaScript);

    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, []);

  const commitments = [
    {
      icon: 'ri-leaf-line',
      title: 'Gestión Ecológica',
      description: 'Reciclamos y reutilizamos todo lo posible. Los materiales no aprovechables los llevamos a puntos limpios autorizados.'
    },
    {
      icon: 'ri-heart-line',
      title: 'Donaciones Solidarias',
      description: 'Los enseres en buen estado los donamos a organizaciones benéficas y familias necesitadas de Barcelona.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Máximo Profesionalismo',
      description: 'Equipo formado, uniformado y con todas las garantías. Tratamos tu hogar con el máximo respeto.'
    },
    {
      icon: 'ri-time-line',
      title: 'Puntualidad Garantizada',
      description: 'Cumplimos los horarios acordados. Tu tiempo es valioso y lo respetamos desde el primer contacto.'
    }
  ];

  const teamMembers = [
    {
      name: 'Carlos Martínez',
      role: 'Fundador y Director',
      experience: '15 años de experiencia',
      description: 'Especialista en valoración de enseres y gestión de equipos.'
    },
    {
      name: 'Ana García',
      role: 'Coordinadora de Operaciones',
      experience: '8 años de experiencia',
      description: 'Experta en logística y atención al cliente personalizada.'
    },
    {
      name: 'Miguel Rodríguez',
      role: 'Jefe de Equipo',
      experience: '12 años de experiencia',
      description: 'Responsable de vaciados complejos y casos especiales.'
    }
  ];

  const whyChooseUs = [
    {
      icon: 'ri-shield-line',
      title: 'Totalmente Asegurados',
      description: 'Seguro de responsabilidad civil y garantía total en todos nuestros servicios.'
    },
    {
      icon: 'ri-flashlight-line',
      title: 'Servicio Rápido',
      description: 'Respuesta en menos de 2 horas y ejecución en 24-48h según disponibilidad.'
    },
    {
      icon: 'ri-star-line',
      title: 'Máxima Confiabilidad',
      description: 'Más de 500 vaciados realizados con éxito y 4.9/5 estrellas en Google.'
    },
    {
      icon: 'ri-money-euro-circle-line',
      title: 'Mejor Relación Calidad-Precio',
      description: 'Valoración justa de enseres y tarifas transparentes sin sorpresas.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat py-32"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://public.readdy.ai/ai/img_res/a6b8042ca9c244b211925ebd79e95642.jpg')`
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Nuestra misión: vaciados rápidos, profesionales y responsables
            </h1>
            <p className="text-xl md:text-2xl leading-relaxed">
              Somos un equipo comprometido con ofrecer el mejor servicio de vaciado en Barcelona, 
              combinando profesionalidad, rapidez y responsabilidad medioambiental. Descubre todos 
              <a href="/servicios" className="text-green-200 hover:text-white font-medium ml-1 mr-1">
                nuestros servicios especializados
              </a>
              y resuelve tus dudas en nuestras 
              <a href="/faq" className="text-green-200 hover:text-white font-medium ml-1">
                preguntas frecuentes
              </a>.
            </p>
          </div>
        </div>
      </section>

      {/* Our Commitment */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Nuestro compromiso contigo y con Barcelona
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Más que un servicio de vaciado, somos tu aliado para una gestión responsable y profesional
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {commitments.map((commitment, index) => (
              <div key={index} className="text-center bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${commitment.icon} text-white text-2xl`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">{commitment.title}</h3>
                <p className="text-gray-600 leading-relaxed">{commitment.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Team */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Conoce a nuestro equipo
            </h2>
            <p className="text-xl text-gray-600">
              Profesionales experimentados que hacen posible cada vaciado exitoso
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {teamMembers.map((member, index) => (
              <div key={index} className="bg-white rounded-xl overflow-hidden shadow-lg">
                <img 
                  src={index === 0 ? 
                    "https://readdy.ai/api/search-image?query=Professional%20Spanish%20male%20business%20owner%20director%20in%20work%20uniform%2C%20friendly%20confident%20smile%2C%20Barcelona%20office%20background%2C%20professional%20headshot%20style%2C%20trustworthy%20appearance%2C%20clean%20modern%20setting%2C%20mature%20experienced%20businessman&width=400&height=400&seq=team-member-1&orientation=squarish" :
                    index === 1 ?
                    "https://readdy.ai/api/search-image?query=Professional%20Spanish%20female%20operations%20coordinator%20businesswoman%20in%20work%20uniform%2C%20friendly%20confident%20smile%2C%20Barcelona%20office%20background%2C%20professional%20headshot%20style%2C%20trustworthy%20appearance%2C%20clean%20modern%20setting%2C%20professional%20businesswoman&width=400&height=400&seq=team-member-2&orientation=squarish" :
                    "https://readdy.ai/api/search-image?query=Professional%20Spanish%20male%20team%20leader%20supervisor%20in%20work%20uniform%2C%20friendly%20confident%20smile%2C%20Barcelona%20office%20background%2C%20professional%20headshot%20style%2C%20trustworthy%20appearance%2C%20clean%20modern%20setting%2C%20experienced%20foreman%20supervisor&width=400&height=400&seq=team-member-3&orientation=squarish"
                  }
                  alt={`${member.name} - ${member.role}`}
                  className="w-full h-64 object-cover object-top"
                />
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">{member.name}</h3>
                  <p className="text-green-600 font-medium mb-2">{member.role}</p>
                  <p className="text-sm text-gray-500 mb-3">{member.experience}</p>
                  <p className="text-gray-600">{member.description}</p>
                </div>
              </div>
            ))}
          </div>
          
          {/* Team Van */}
          <div className="bg-white rounded-xl overflow-hidden shadow-lg">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20white%20van%20with%20VACIADO%20PRO%20text%20logo%20clearly%20visible%20on%20side%2C%20Barcelona%20street%20setting%2C%20clean%20modern%20furniture%20removal%20service%20vehicle%2C%20Spanish%20company%20branding%2C%20well-maintained%20commercial%20truck%20with%20company%20name%20prominently%20displayed&width=600&height=400&seq=company-van-branded&orientation=landscape"
                alt="Nuestra furgoneta de servicio VACIADO PRO"
                className="w-full h-64 lg:h-full object-cover object-top"
              />
              <div className="p-8 flex flex-col justify-center">
                <h3 className="text-2xl font-semibold text-gray-800 mb-4">
                  Equipamiento profesional
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Contamos con vehículos propios perfectamente equipados para cualquier tipo de vaciado. 
                  Nuestras furgonetas están preparadas para transportar desde objetos delicados hasta 
                  muebles de gran tamaño con total seguridad. Conoce todos los detalles de 
                  <a href="/servicios" className="text-green-600 hover:text-green-700 font-medium ml-1">
                    nuestros servicios
                  </a>.
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center gap-3">
                    <i className="ri-check-line text-green-600"></i>
                    Vehículos propios y asegurados
                  </li>
                  <li className="flex items-center gap-3">
                    <i className="ri-check-line text-green-600"></i>
                    Material de protección y embalaje
                  </li>
                  <li className="flex items-center gap-3">
                    <i className="ri-check-line text-green-600"></i>
                    Herramientas especializadas
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              ¿Por qué elegirnos?
            </h2>
            <p className="text-xl text-gray-600">
              Las razones que nos convierten en la mejor opción para tu vaciado en Barcelona
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {whyChooseUs.map((reason, index) => (
              <div key={index} className="flex items-start gap-6 bg-gray-50 rounded-xl p-8">
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className={`${reason.icon} text-white text-2xl`}></i>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">{reason.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{reason.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Values */}
      <section className="py-20 bg-green-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
              Nuestros valores
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div className="text-center">
                <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-hand-heart-line text-white text-3xl"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-3">Confianza</h3>
                <p className="text-gray-600">Transparencia total en precios y procesos</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-recycle-line text-white text-3xl"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-3">Sostenibilidad</h3>
                <p className="text-gray-600">Compromiso real con el medio ambiente</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-customer-service-line text-white text-3xl"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-3">Excelencia</h3>
                <p className="text-gray-600">Superamos las expectativas en cada servicio</p>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-2xl font-semibold text-gray-800 mb-4">
                Más de 500 familias satisfechas
              </h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Desde nuestros inicios, hemos ayudado a cientos de familias y empresas en Barcelona 
                a resolver sus necesidades de vaciado de forma rápida, profesional y responsable. 
                Contribuimos a solucionar el problema de la 
                <a href="https://es.wikipedia.org/wiki/Vivienda_vac%C3%ADa" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:text-green-700 font-medium">
                  vivienda vacía
                </a>
                en la ciudad, facilitando que los propietarios puedan poner sus inmuebles en el mercado.
                Cada cliente satisfecho es nuestro mejor aval. Si tienes alguna duda, consulta nuestras 
                <a href="/faq" className="text-green-600 hover:text-green-700 font-medium ml-1 mr-1">
                  preguntas frecuentes
                </a>
                o 
                <a href="/contacto" className="text-green-600 hover:text-green-700 font-medium ml-1">
                  contáctanos directamente
                </a>.
              </p>
              <Button href="https://wa.me/34690220944" variant="primary" target="_blank">
                Únete a Nuestros Clientes Satisfechos
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-green-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            ¿Listo para trabajar con nosotros?
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Contacta con nuestro equipo y descubre por qué somos la opción preferida 
            para vaciados en Barcelona. Consulta nuestros 
            <a href="/servicios" className="text-green-200 hover:text-white font-medium ml-1 mr-1">
              servicios especializados
            </a>
            y obtén tu presupuesto personalizado.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              href="https://wa.me/34690220944?text=Hola, quiero trabajar con VaciadoPro Barcelona" 
              variant="primary" 
              size="lg"
              className="flex items-center gap-3"
              target="_blank"
            >
              <i className="ri-whatsapp-line text-xl"></i>
              Empezar Proyecto
            </Button>
            <Button 
              href="/contacto" 
              variant="secondary" 
              size="lg"
              className="flex items-center gap-3 !bg-white !text-green-600 hover:!bg-gray-100 border-2 border-green-600"
            >
              <i className="ri-mail-line text-xl"></i>
              Más Información
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}